/** Estrutura compartilhada da navegação, sem alterar workflows ou telemetria. */
export const NAVIGATION_ICONS = ["layers", "factory", "flask", "flame", "wind", "water", "energy"] as const;
export type NavigationIcon = typeof NAVIGATION_ICONS[number];
export interface NavigationGroup {
  id: string;
  name: string;
  section: string | null;
  icon: NavigationIcon;
  workflowIds: string[];
}
export interface NavigationConfig { revision: number; groups: NavigationGroup[] }
export const NAVIGATION_LIMITS = { groups: 64, workflows: 500, label: 80, workflowId: 120, revision: 2_147_483_647 } as const;

export class NavigationValidationError extends Error {
  constructor() { super("navigation_invalid"); this.name = "NavigationValidationError"; }
}
function invalid(): never { throw new NavigationValidationError(); }
function record(value: unknown, keys: readonly string[]): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) invalid();
  const result = value as Record<string, unknown>;
  if (Object.keys(result).length !== keys.length || keys.some(key => !Object.hasOwn(result, key))) invalid();
  return result;
}
function hasControlCharacters(value: string): boolean {
  return Array.from(value).some(character => {
    const code = character.charCodeAt(0);
    return code < 32 || (code >= 127 && code <= 159);
  });
}
function label(value: unknown): string {
  if (typeof value !== "string" || hasControlCharacters(value)) invalid();
  const result = value.trim();
  if (!result || result.length > NAVIGATION_LIMITS.label) invalid();
  return result;
}

/** Preserva a ordem enviada; só normaliza espaços externos dos rótulos. */
export function parseNavigationConfig(value: unknown): NavigationConfig {
  const input = record(value, ["revision", "groups"]);
  if (!Number.isInteger(input.revision) || (input.revision as number) < 0 || (input.revision as number) > NAVIGATION_LIMITS.revision) invalid();
  if (!Array.isArray(input.groups) || input.groups.length > NAVIGATION_LIMITS.groups) invalid();
  const groupIds = new Set<string>(), workflowIds = new Set<string>();
  const groups = input.groups.map(value => {
    const group = record(value, ["id", "name", "section", "icon", "workflowIds"]);
    if (typeof group.id !== "string" || !/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/.test(group.id) || groupIds.has(group.id)) invalid();
    groupIds.add(group.id);
    if (!NAVIGATION_ICONS.includes(group.icon as NavigationIcon)) invalid();
    if (!Array.isArray(group.workflowIds) || group.workflowIds.length > NAVIGATION_LIMITS.workflows) invalid();
    const ids = group.workflowIds.map(id => {
      if (typeof id !== "string" || !id || id !== id.trim() || id.length > NAVIGATION_LIMITS.workflowId
        || hasControlCharacters(id) || workflowIds.has(id)) invalid();
      workflowIds.add(id);
      if (workflowIds.size > NAVIGATION_LIMITS.workflows) invalid();
      return id;
    });
    return { id: group.id, name: label(group.name), section: group.section === null ? null : label(group.section),
      icon: group.icon as NavigationIcon, workflowIds: ids };
  });
  return { revision: input.revision as number, groups };
}
